import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { submitTeamScores } from "../services/functional_summaries";
import moment from "moment";
import { NOT_APPLICABLE_LABEL, toScoreValue } from "../utils/scoreState";

const useSubmitTeamScores = () => {
  const [selectedScores, setSelectedScores] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);

  const formatDateWithZeroTime = (date) => {
    if (!date) return null;
    const formattedDate = moment(date).local().format("YYYY-MM-DD");
  
    return formattedDate;
  };

  const mutation = useMutation({
    mutationFn: submitTeamScores,
    onSuccess: (data) => {
      // Reset selected scores after successful submission
      setSelectedScores({});
      setValidationErrors([]);
    },
    onError: (error) => {
      console.error("Error submitting team scores:", error);
    },
  });

  const updateScore = (subheadingId, score) => {
    setSelectedScores((prev) => ({
      ...prev,
      [subheadingId]: score,
    }));

    setValidationErrors((prev) =>
      prev.filter((error) => error.subheadingId !== subheadingId)
    );
  };

  const clearScore = (subheadingId) => {
    setSelectedScores((prev) => {
      const nextScores = { ...prev };
      delete nextScores[subheadingId];
      return nextScores;
    });

    setValidationErrors((prev) =>
      prev.filter((error) => error.subheadingId !== subheadingId)
    );
  };

  const validateScores = (requiredSubheadings) => {
    // No validation required - team scores are optional
    setValidationErrors([]);
    return true;
  };

  const submitScores = async (
    patientId,
    requiredSubheadings,
    startDate,
    endDate,
    { useMedianForUnscored = false } = {}
  ) => {
    // The batch is filed against this patient. It used to be inferred from the
    // caller's assignments, which misfiled scores whenever the provider had more
    // than one patient, so the API now rejects a request without it.
    const numericPatientId = Number(patientId);
    if (!patientId || Number.isNaN(numericPatientId)) {
      console.error("Cannot submit team scores without a patientId");
      return false;
    }

    // Every subheading on screen is sent, not just the unscored ones: the API
    // treats the request as a partial update, so anything omitted keeps what it
    // already had. Re-sending the saved value makes the payload the full
    // intended state of the batch rather than a diff the client has to get right.
    const scoresArray = requiredSubheadings.map((subheading) => {
      const scoreValue = selectedScores[subheading.id];

      // Picking "N/A" is an explicit answer: it sends notApplicable with no
      // score.
      if (scoreValue === NOT_APPLICABLE_LABEL) {
        return {
          subheadingId: subheading.id,
          score: null,
          notApplicable: true,
        };
      }

      // Every value is checked against the scale its own heading is scored
      // on: a batch spans headings that may be on different ranges, and the
      // API rejects a score its heading's scale does not define.
      const scale = subheading.scoringScale;
      const pickedScore = toScoreValue(scoreValue, scale);
      if (pickedScore !== null) {
        return {
          subheadingId: subheading.id,
          score: pickedScore,
          notApplicable: false,
        };
      }

      // Nothing picked this time, so resend whatever is already saved. The
      // median is NOT substituted by default: filling in a score the team never
      // chose put numbers on rows the user had left blank, and the median
      // itself reflects whatever filters were active at the time. The caller
      // opts in via useMedianForUnscored, which is the user answering the
      // "use the medians as the team scores?" prompt.
      if (subheading.teamScoreNotApplicable) {
        return {
          subheadingId: subheading.id,
          score: null,
          notApplicable: true,
        };
      }

      const savedScore = toScoreValue(subheading.teamScore, scale);
      if (savedScore === null && useMedianForUnscored) {
        // Medians are decimals here (§3.2), so floor onto the heading's scale
        // — the same rounding the API applies to its own median.
        const median = toScoreValue(
          subheading.median == null ? null : Math.floor(subheading.median),
          scale
        );
        if (median !== null) {
          return {
            subheadingId: subheading.id,
            score: median,
            notApplicable: false,
          };
        }
      }

      return {
        subheadingId: subheading.id,
        score: savedScore,
        notApplicable: false,
      };
    });

    const payload = {
      patientId: numericPatientId,
      scores: scoresArray,
      startDate: formatDateWithZeroTime(startDate),
      endDate: formatDateWithZeroTime(endDate),
    };

    try {
      const response = await mutation.mutateAsync(payload);
      // Errors are not wrapped — they come back flat with no `data` — and the
      // service does not check response.ok, so a rejected write resolves here
      // rather than throwing. A missing id is the only reliable tell.
      const batchId = response?.data?.id;
      if (!batchId) {
        console.error("Team scores were not saved:", response?.message);
        return false;
      }
      // Truthy, so existing `if (success)` callers are unaffected, and the id
      // is available to store on the care plan.
      return batchId;
    } catch (error) {
      return false;
    }
  };

  const getSelectedScore = (subheadingId) => {
    return selectedScores[subheadingId] || null;
  };

  // The scale is passed explicitly by callers that hold the heading; rows that
  // were flattened for submission carry it on themselves.
  const getEffectiveTeamScore = (subheading, scale = subheading?.scoringScale) => {
    const selectedScore = selectedScores[subheading.id];
    if (selectedScore) {
      return selectedScore;
    }
    if (subheading?.teamScoreNotApplicable) {
      return NOT_APPLICABLE_LABEL;
    }
    // If no manual selection, return the saved team score, falling back to the
    // median as the effective one.
    return (
      toScoreValue(subheading.teamScore, scale) ??
      toScoreValue(subheading.median, scale)
    );
  };

  const hasValidationError = (subheadingId) => {
    return validationErrors.some(
      (error) => error.subheadingId === subheadingId
    );
  };

  const getValidationError = (subheadingId) => {
    const error = validationErrors.find(
      (error) => error.subheadingId === subheadingId
    );
    return error ? error.message : null;
  };

  const shouldShowDropdown = (subheading, scale = subheading?.scoringScale) => {
    // An N/A team score is an answer, not an absence. It comes back with a null
    // score, so without this flag it read as "never scored", the dropdown was
    // offered again and the next save replaced the team's N/A with a number.
    if (subheading?.teamScoreNotApplicable) {
      return false;
    }
    // Hide dropdown if subheading has a valid team score
    return toScoreValue(subheading.teamScore, scale) === null;
  };

  return {
    selectedScores,
    validationErrors,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    updateScore,
    clearScore,
    submitScores,
    getSelectedScore,
    getEffectiveTeamScore,
    hasValidationError,
    getValidationError,
    shouldShowDropdown,
    validateScores,
  };
};

export default useSubmitTeamScores;
